import React, { useState, useEffect } from 'react';
import { Layout, Table, Card, Row, Col, Statistic, Progress, Typography, Spin, Empty } from 'antd';
import { FieldTimeOutlined, CalendarOutlined, LineChartOutlined } from '@ant-design/icons';
import moment from 'moment';
import getUserIdFromToken from '../../Utils/authUtils';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';

const { Content } = Layout;
const { Text } = Typography;

const MonthlyHoursSummary = () => {
  const { t } = useTranslation();
  const userId = getUserIdFromToken();
  const [hoursData, setHoursData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalHours: 0,
    averageMonthly: 0,
    highestMonth: { period: '', hours: 0 }
  });

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchMonthlyHours = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classes/teacher/monthlyhours/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data.map((item) => ({
          ...item,
          display_hours: item.total_hours / 60,
          original_minutes: item.total_hours,
          originalMonthYear: item.month_year,
        }));

        setHoursData(data);
        calculateStats(data);
      } catch (err) {
        console.error('Error fetching monthly hours:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyHours();
  }, [userId, API_URL]);

  const calculateStats = (data) => {
    if (!data || data.length === 0) return;
    const total = data.reduce((sum, d) => sum + d.display_hours, 0);
    const highest = data.reduce((prev, curr) => (curr.display_hours > prev.display_hours ? curr : prev));
    setStats({
      totalHours: total.toFixed(1),
      averageMonthly: (total / data.length).toFixed(1),
      highestMonth: {
        period: highest.month_year,
        hours: highest.display_hours.toFixed(1),
      },
    });
  };

  const columns = [
    {
      title: t('Month'),
      dataIndex: 'month_year',
      key: 'month_year',
      render: (val) => moment(val, 'MMM YYYY').format('MMM YYYY'),
    },
    {
      title: t('Total Hours'),
      key: 'total_hours',
      render: (_, record) => (
        <span title={`${record.original_minutes} minutes`}>
          {record.display_hours.toFixed(1)}h
        </span>
      ),
      sorter: (a, b) => a.display_hours - b.display_hours,
    },
    {
      title: t('Progress'),
      key: 'progress',
      render: (_, record) => (
        <Progress
          percent={Math.min(100, (record.display_hours / 120) * 100)}
          size="small"
          strokeColor={{
            '0%': '#73d13d',
            '50%': '#40a9ff',
            '100%': '#9254de',
          }}
          showInfo={false}
        />
      ),
    },
  ];

  const statCardStyle = {
    background: 'linear-gradient(135deg, #e6f7ff, #ffffff)',
    border: '1px solid #91d5ff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.15)'
  };

  return (
    <Layout>
      <Sidebar />
      <Layout>
        <Header />
        <Content style={{ padding: '24px' }}>
          {loading ? (
            <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />
          ) : (
            <>
              {/* Stats Summary */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card style={statCardStyle}>
                    <Statistic
                      title={t('Total Hours')}
                      value={stats.totalHours}
                      suffix="h"
                      prefix={<FieldTimeOutlined style={{ color: '#1890ff' }} />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card style={{ ...statCardStyle, background: 'linear-gradient(135deg, #f6ffed, #ffffff)' }}>
                    <Statistic
                      title={t('Monthly Average')}
                      value={stats.averageMonthly}
                      suffix="h"
                      prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card style={{ ...statCardStyle, background: 'linear-gradient(135deg, #f9f0ff, #ffffff)' }}>
                    <Statistic
                      title={t('Highest Month')}
                      value={stats.highestMonth.hours}
                      suffix="h"
                      prefix={<LineChartOutlined style={{ color: '#722ed1' }} />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                    <Text type="secondary">{stats.highestMonth.period}</Text>
                  </Card>
                </Col>
              </Row>

              {/* Data Table */}
              <Card
                title={t('Monthly Hours Summary')}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '10px',
                  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                {hoursData.length > 0 ? (
                  <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <Table
                    columns={columns}
                    dataSource={hoursData}
                    rowKey="originalMonthYear"
                    pagination={false}
                    scroll={{ x: true }} // enables horizontal scroll only if needed
                  />
                </div>
                
                ) : (
                  <Empty description={t('No data available')} />
                )}
              </Card>
            </>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(MonthlyHoursSummary, ['teacher']);
